import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  alpha,
} from '@mui/material';
import {
  BlockOutlined as BlockedIcon,
} from '@mui/icons-material';

interface NetworkBlockedModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Shown when a mutation (PATCH/POST/etc.) fails with a bare network error —
 * no HTTP response at all, not a 4xx/5xx. In practice this has repeatedly
 * turned out to be a browser extension (ad blocker / privacy tool) treating
 * PATCH/DELETE as "suspicious" and silently dropping the request, across
 * unrelated endpoints and both local and production. Triggered from
 * apiClient.ts's `network-request-blocked` window event.
 */
export const NetworkBlockedModal: React.FC<NetworkBlockedModalProps> = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(12px)',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
          }
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
        }
      }}
    >
      <Box
        sx={{
          background: (theme) => `linear-gradient(135deg, ${theme.palette.background.chipWarning} 0%, ${alpha(theme.palette.background.chipWarning, 0.85)} 100%)`,
          color: 'white',
          p: 3,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}
        >
          <BlockedIcon sx={{ fontSize: 40 }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Acción bloqueada
        </Typography>
      </Box>

      <DialogContent sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Algo en tu navegador bloqueó esta acción antes de que llegara a nuestros
          servidores — normalmente un bloqueador de anuncios o una extensión de privacidad.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Desactiva el bloqueador de anuncios para este sitio y vuelve a intentar. Si
          el problema continúa, revisa tu conexión a internet.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 0, justifyContent: 'center' }}>
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          sx={{
            textTransform: 'none',
            px: 4,
            py: 1.5,
            fontWeight: 600,
            bgcolor: (theme) => theme.palette.background.buttonBg,
            color: (theme) => theme.palette.background.buttonText,
            '&:hover': {
              bgcolor: (theme) => theme.palette.background.buttonBg,
              filter: 'brightness(0.9)',
            }
          }}
        >
          Entendido
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NetworkBlockedModal;
